import type { RouteRecordRaw } from "vue-router";

export enum RouteNames {
  Dispatch = "dispatch",
  Projects = "projects",
  ProjectDetail = "project_detail",
  EstimatorReportDetail = "estimator_report_detail",
  CrossValidationReportDetail = "cross_validation_report_detail",
  ComparisonBuilder = "comparison_builder",
  Comparison = "comparison",
  WorkspaceDetail = "workspace_detail",
  Onboarding = "onboarding",
  JupyterLite = "jupyterlite",
  Login = "login",
  LoginSuccess = "login_success",
  DeviceToken = "device_token",
  Logout = "logout",
  NotFound = "not_found",
  Crash = "crash",
  Components = "components",
}

export enum ProjectDetailSections {
  EstimatorReports = "estimators",
  CrossValidationReports = "cross-validations",
  Comparisons = "comparisons",
}

const devOnlyRoutes: RouteRecordRaw[] = import.meta.env.DEV
  ? [
      {
        path: "/components",
        name: RouteNames.Components,
        component: () => import("@/views/components/ComponentsView.vue"),
        meta: {
          requiresAuthentication: false,
        },
      },
      {
        path: "/jupyter",
        name: RouteNames.JupyterLite,
        component: () => import("@/views/jupyterlite/JupyterLiteView.vue"),
        meta: {
          requiresAuthentication: true,
        },
      },
    ]
  : [];

export const routes: RouteRecordRaw[] = [
  ...devOnlyRoutes,
  {
    path: "/",
    name: RouteNames.Dispatch,
    component: () => import("@/views/dispatch/DispatchView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId",
    name: RouteNames.Projects,
    component: () => import("@/views/projects/ProjectsListView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName",
    redirect: (to) => ({
      name: RouteNames.ProjectDetail,
      params: {
        workspacePublicId: to.params.workspacePublicId as string,
        projectName: to.params.projectName as string,
        section: ProjectDetailSections.EstimatorReports,
      },
    }),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName/:section/:itemId?",
    name: RouteNames.ProjectDetail,
    component: () => import("@/views/projects/ProjectDetailView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: `/:workspacePublicId/:projectName/${ProjectDetailSections.EstimatorReports}/:reportId`,
    name: RouteNames.EstimatorReportDetail,
    component: () => import("@/views/reports/EstimatorReportView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: `/:workspacePublicId/:projectName/${ProjectDetailSections.CrossValidationReports}/:reportId`,
    name: RouteNames.CrossValidationReportDetail,
    component: () => import("@/views/reports/CrossValidationReportView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName/comparisons/builder",
    name: RouteNames.ComparisonBuilder,
    component: () => import("@/views/comparison/EstimatorReportComparisonBuilderView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName/comparisons/:comparisonId",
    name: RouteNames.Comparison,
    component: () => import("@/views/comparison/EstimatorReportComparisonView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/workspace/:workspaceId",
    name: RouteNames.WorkspaceDetail,
    component: () => import("@/views/workspaces/WorkspaceDetailView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/login",
    name: RouteNames.Login,
    component: () => import("@/views/authentication/LoginView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/login/success",
    name: RouteNames.LoginSuccess,
    component: () => import("@/views/authentication/LoginSuccessView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/device",
    name: RouteNames.DeviceToken,
    component: () => import("@/views/authentication/DeviceTokenView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/logout",
    name: RouteNames.Logout,
    component: () => import("@/views/authentication/LogoutView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/not-found",
    name: RouteNames.NotFound,
    component: () => import("@/views/NotFoundView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/onboarding",
    name: RouteNames.Onboarding,
    component: () => import("@/views/onboarding/OnboardingView.vue"),
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/crash",
    name: RouteNames.Crash,
    component: () => import("@/views/CrashView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
];
